import type React from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import SumsubTransactionMonitoringStatusBadge from '../../../../../../components/SumsubTransactionMonitoringStatusBadge'
import TransferStatusBadge from '../../../../../../components/TransferStatusBadge'
import { INVESTMENT_PACKAGE_LABELS } from '../../../../../../constants/investmentPackages'
import type { AdminFundDepositDto } from '../../../../../../types/coreApi-types'
import { formatNumber, formatUtcDate } from '../../../../../../utils/formatters'

interface Props {
  isOpen: boolean
  onClose: () => void
  deposit: AdminFundDepositDto | null
}

const SectionHeader: React.FC<{ title: string; color: string }> = ({
  title,
  color,
}) => (
  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
    <div className={`w-2 h-2 ${color} rounded-full`}></div>
    {title}
  </h3>
)

const InfoRow: React.FC<{
  label: string
  value: string | React.ReactNode
  isLast?: boolean
}> = ({ label, value, isLast = false }) => (
  <div
    className={`flex justify-between items-center gap-4 py-2 ${
      !isLast ? 'border-b border-gray-200 dark:border-gray-600' : ''
    }`}
  >
    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
      {label}
    </span>
    <span className="text-sm font-mono text-gray-900 dark:text-gray-100 text-right break-all">
      {value}
    </span>
  </div>
)

const FundDepositSummaryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  deposit,
}) => {
  if (!deposit) {
    return (
      <DefaultModal
        isOpen={isOpen}
        onClose={onClose}
        isLoading={false}
        title="Portfolio Deposit"
      >
        <div className="p-5 pt-0 text-sm text-gray-500">No deposit selected.</div>
      </DefaultModal>
    )
  }

  const packageLabel = deposit.investmentPackage
    ? INVESTMENT_PACKAGE_LABELS[deposit.investmentPackage]
    : '—'

  const amount = formatNumber(deposit.amount, deposit.currency)
  const amountInEur = formatNumber(
    deposit.amount * deposit.currencyToEurRate,
    { currencyType: 'FiatCurrency', isoCode: 'EUR' }
  )

  const fromBank = deposit.fromBankAccount
  const fromWallet = deposit.fromWallet
  const toBank = deposit.toBankAccount

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={false}
      title="Portfolio Deposit"
      maxW="640px"
      renderTopRight={<TransferStatusBadge transferStatus={deposit.transferStatus} />}
    >
      <div className="px-5 pb-5 space-y-6">
        <section>
          <SectionHeader title="Overview" color="bg-blue-500" />
          <InfoRow label="Investor" value={deposit.user?.name ?? '—'} />
          <InfoRow label="Package" value={packageLabel} />
          <InfoRow
            label="Amount"
            value={
              <span>
                {amount}{' '}
                {deposit.currency.isoCode !== 'EUR' && (
                  <span className="text-gray-500">({amountInEur})</span>
                )}
              </span>
            }
          />
          <InfoRow
            label="EUR rate"
            value={`1 ${deposit.currency.isoCode} = ${formatNumber(
              deposit.currencyToEurRate,
              { currencyType: 'FiatCurrency', isoCode: 'EUR' },
              6
            )}`}
            isLast
          />
        </section>

        <section>
          <SectionHeader title="Timing" color="bg-purple-500" />
          <InfoRow
            label="Created"
            value={formatUtcDate(deposit.createdAt)}
          />
          <InfoRow
            label="Transferred"
            value={deposit.transferredAt ? formatUtcDate(deposit.transferredAt) : '—'}
            isLast
          />
        </section>

        <section>
          <SectionHeader title="Identifiers" color="bg-emerald-500" />
          <InfoRow label="Deposit ID" value={deposit.id} />
          <InfoRow
            label="Bank deposit ID"
            value={deposit.bankDepositUniqueId || '—'}
            isLast={!deposit.transactionHash}
          />
          {deposit.transactionHash && (
            <InfoRow
              label="Tx hash"
              value={deposit.transactionHash}
              isLast
            />
          )}
        </section>

        {(fromBank || fromWallet) && (
          <section>
            <SectionHeader title="Source" color="bg-amber-500" />
            {fromBank && (
              <>
                <InfoRow label="From bank" value={fromBank.name ?? '—'} />
                <InfoRow
                  label="IBAN"
                  value={fromBank.bankAccountDetails.iban ?? '—'}
                />
                <InfoRow
                  label="BIC"
                  value={fromBank.bankAccountDetails.bic ?? '—'}
                  isLast
                />
              </>
            )}
            {fromWallet && (
              <>
                <InfoRow label="From wallet" value={fromWallet.address} />
                <InfoRow
                  label="Network"
                  value={fromWallet.network?.name ?? '—'}
                  isLast
                />
              </>
            )}
          </section>
        )}

        {toBank && (
          <section>
            <SectionHeader title="Destination" color="bg-teal-500" />
            <InfoRow label="To bank" value={toBank.name ?? '—'} />
            <InfoRow
              label="IBAN"
              value={toBank.bankAccountDetails.iban ?? '—'}
              isLast
            />
          </section>
        )}

        {deposit.sumsubTransactionMonitoringStatus && (
          <section>
            <SectionHeader title="Compliance" color="bg-rose-500" />
            <InfoRow
              label="Sumsub monitoring"
              value={
                <SumsubTransactionMonitoringStatusBadge
                  status={deposit.sumsubTransactionMonitoringStatus}
                />
              }
              isLast={!deposit.sumsubOnHoldReason}
            />
            {deposit.sumsubOnHoldReason && (
              <InfoRow
                label="On-hold reason"
                value={deposit.sumsubOnHoldReason}
                isLast
              />
            )}
          </section>
        )}
      </div>
    </DefaultModal>
  )
}

export default FundDepositSummaryModal
